import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/store';
import type { WorldBossSummary } from '../../lib/types';
import { Bar, Panel } from '../ui';
import { formatNumber } from '../../lib/format';

/**
 * The Home page's World Boss strip — design PART 34.
 *
 * It renders **nothing at all** when no boss is running, exactly as
 * `EventPanel` does: a permanent "no World Boss" panel is a hole in the Home
 * page in exchange for information nobody needed.
 *
 * When one *is* running it is deliberately the loudest thing on the page short
 * of taking it over — PART 34 asks for urgency without covering the whole site.
 * So it gets a sprite, an HP bar and one button, and stops. The rank, the
 * damage and the attempts are there because they are the three numbers that
 * decide whether somebody clicks; everything else is on the page the button
 * goes to.
 *
 * The button's *wording* changes with what the player has actually done, which
 * is the small thing that makes it useful: sending somebody to a page and then
 * asking them a question is worse than asking it here.
 */
export function WorldBossPanel() {
  const state = useAsync<{ boss: WorldBossSummary | null }>(() => api.get('/world-boss/summary'));

  const boss = state.data?.boss ?? null;
  if (state.loading || !boss || boss.status !== 'LIVE') return null;

  const noAttempts = boss.attempts.available === 0;

  return (
    <Panel
      title={`⚡ World Boss: ${boss.bossName}`}
      hint={countdown(boss.secondsRemaining)}
      className={`wb-panel wb-panel--${boss.types[0] ?? 'normal'}`}
      action={
        <Link to="/world-boss" className="btn btn--sm">
          {!boss.hasFighter ? 'Choose a fighter' : noAttempts ? 'View' : `Challenge ${boss.bossName}`}
        </Link>
      }
    >
      <div className="wb-panel__body">
        <img className="pixel wb-panel__sprite" src={boss.sprite ?? ''} alt="" width={72} height={72} />

        <div className="wb-panel__meter">
          <Bar ratio={boss.hp.percent / 100} variant="egg" tall />
          <div className="wb-panel__figures">
            <span className="mono">
              {compact(boss.hp.remaining)} / {compact(boss.hp.max)}
            </span>
            <span className="mono">{boss.hp.percent.toFixed(1)}%</span>
          </div>

          <dl className="wb-panel__stats">
            <div>
              <dt>Your rank</dt>
              <dd className="mono">{boss.yourRank ? `#${boss.yourRank}` : '—'}</dd>
            </div>
            <div>
              <dt>Your damage</dt>
              <dd className="mono">{compact(boss.yourDamage)}</dd>
            </div>
            <div>
              <dt>Attempts</dt>
              <dd className="mono">
                {boss.attempts.available} / {boss.attempts.perDay}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </Panel>
  );
}

/**
 * "32.8M", not "32,841,209".
 *
 * A pool this size is read as a *shape* rather than a figure, and eight digits
 * in a Home panel is eight digits nobody parses. The exact number is on the
 * boss's own page, where somebody is actually looking at it.
 */
function compact(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 10_000) return `${Math.round(value / 1000)}K`;
  return formatNumber(value);
}

function countdown(seconds: number): string {
  if (seconds <= 0) return 'Ending now';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  if (days > 0) return `Ends in ${days}d ${hours}h`;
  const minutes = Math.floor((seconds % 3600) / 60);
  return `Ends in ${hours}h ${minutes}m`;
}
